// ============================================
// 2026-09-14｜组合模型小部件操作（阶段 A+B+C 公共逻辑）
// 供右键菜单（SubObjectContextMenu）与快捷键（SceneEditor）共用
// 语义：复制=克隆副本（材质深拷贝）；剪切/删除=软隐藏（可粘贴/Ctrl+Z 恢复、随 SubObjectConfig.visible 持久化）
// 历史：删除/剪切/重命名前后推送含 subObjectConfigs 的快照（Ctrl+Z 可撤销）
// ============================================

import { message, Modal } from 'antd';
import { modelRefsRegistry } from '../stores/modelRefsRegistry';
import useSceneStore from '../stores/sceneStore';
import { cameraControlsRegistry } from '../stores/cameraControlsStore';
import { setSubObjectClipboard, getSubObjectClipboard } from '../stores/subObjectMenuStore';
import { useSubObjectMenuStore } from '../stores/subObjectMenuStore';
import type { SubObjectNode } from '@dtwin/shared';

/** 从构件树查部件名（菜单/提示共用） */
export function getSubObjectName(modelId: string, uuid: string): string {
  const tree = useSceneStore.getState().subObjectsMap[modelId];
  let found = '部件';
  const walk = (nodes: SubObjectNode[]) => {
    (nodes || []).forEach((n) => {
      if (n.uuid === uuid) found = n.name;
      walk(n.children ?? []);
    });
  };
  if (tree) walk(tree);
  return found;
}

/** 2026-09-14：确保目标部件在配置中有"可见"记录（删除前调用——使撤销快照包含删除前状态，
 * 否则从未操作过的部件删除时新增 visible:false 配置、快照不含 → undo 回滚后配置消失 → 无法恢复显示） */
function ensureVisibleConfig(modelId: string, uuid: string) {
  const st = useSceneStore.getState();
  const list = st.subObjectConfigs[modelId] ?? [];
  const cur = list.find((c) => c.uuid === uuid);
  if (!cur || cur.visible !== true) {
    st.updateSubObjectConfig(modelId, { uuid, name: getSubObjectName(modelId, uuid), visible: true });
  }
}

/** 从构件树查部件的子部件数（删除确认用；顶层部件=整个模型） */
export function getSubObjectChildCount(modelId: string, uuid: string): number {
  const tree = useSceneStore.getState().subObjectsMap[modelId];
  let count = 0;
  const walk = (nodes: SubObjectNode[]): boolean => {
    for (const n of nodes || []) {
      if (n.uuid === uuid) {
        count = n.children?.length ?? 0;
        return true;
      }
      if (walk(n.children ?? [])) return true;
    }
    return false;
  };
  if (tree) walk(tree);
  return count;
}

/** 复制部件（克隆副本并选中；复制不入撤销历史——副本为运行时对象） */
export function copySubObject(modelId: string, uuid: string): boolean {
  const handle = modelRefsRegistry.get(modelId);
  const name = getSubObjectName(modelId, uuid);
  const newUuid = handle?.cloneSubObject?.(uuid);
  if (!newUuid) {
    message.error('复制失败（部件不存在）');
    return false;
  }
  setSubObjectClipboard({ op: 'copy', modelId, uuid, name });
  useSceneStore.getState().selectSubObject({ modelId, uuid: newUuid });
  message.success(`已复制部件「${name}」`);
  return true;
}

/** 剪切部件（软隐藏，可 Ctrl+V / Ctrl+Z 恢复） */
export function cutSubObject(modelId: string, uuid: string): void {
  runWithChildConfirm(modelId, uuid, '剪切', "已剪切", () => {
    const handle = modelRefsRegistry.get(modelId);
    const name = getSubObjectName(modelId, uuid);
    const ok = handle?.removeSubObject?.(uuid);
    if (!ok) {
      message.error('剪切失败');
      return;
    }
    setSubObjectClipboard({ op: 'cut', modelId, uuid, name });
    useSceneStore.getState().selectSubObject(null);
    message.success(`已剪切部件「${name}」（Ctrl+V / Ctrl+Z 可恢复）`);
  });
}

/** 删除部件（软隐藏，可 Ctrl+V / Ctrl+Z 恢复；持久化随 SubObjectConfig.visible） */
export function deleteSubObject(modelId: string, uuid: string): void {
  runWithChildConfirm(modelId, uuid, '删除', '已删除', () => {
    const handle = modelRefsRegistry.get(modelId);
    const name = getSubObjectName(modelId, uuid);
    const ok = handle?.removeSubObject?.(uuid);
    if (!ok) {
      message.error('删除失败');
      return;
    }
    setSubObjectClipboard({ op: 'cut', modelId, uuid, name });
    useSceneStore.getState().selectSubObject(null);
    message.success(`已删除部件「${name}」（Ctrl+V / Ctrl+Z 可恢复）`);
  });
}

/**
 * 删除/剪切统一入口：
 * - 含子部件（或顶层部件）时二次确认（避免误删整块，用户反馈"删除某部件整个模型消失"）
 * - 执行前推送含 subObjectConfigs 的历史快照（Ctrl+Z 可撤销）
 */
function runWithChildConfirm(modelId: string, uuid: string, verb: string, doneVerb: string, run: () => void) {
  const name = getSubObjectName(modelId, uuid);
  const children = getSubObjectChildCount(modelId, uuid);
  const exec = () => {
    // 撤销快照：记录操作前状态（含小部件配置）——先确保目标有"可见"配置（保证快照完整、撤销可恢复）
    ensureVisibleConfig(modelId, uuid);
    useSceneStore.getState().pushSubObjectSnapshot();
    run();
  };
  if (children > 0) {
    Modal.confirm({
      title: `${verb}部件`,
      content: `「${name}」包含 ${children} 个子部件，${verb}后连同子部件一并隐藏（可 Ctrl+Z 撤销 / Ctrl+V 恢复）。确认${verb}？`,
      okText: verb,
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: exec,
    });
    return;
  }
  exec();
}

/** 粘贴（剪切剪贴板 → 恢复显示；复制剪贴板 → 再克隆一份） */
export function pasteSubObject(): boolean {
  const clip = getSubObjectClipboard();
  if (!clip) {
    message.info('剪贴板为空：请先复制/剪切部件');
    return false;
  }
  const handle = modelRefsRegistry.get(clip.modelId);
  if (clip.op === 'cut') {
    // 恢复显示（软删除可逆）：入撤销历史
    useSceneStore.getState().pushSubObjectSnapshot();
    const ok = handle?.restoreSubObject?.(clip.uuid);
    if (!ok) {
      message.error('恢复失败（部件可能已被移除）');
      return false;
    }
    setSubObjectClipboard(null);
    useSceneStore.getState().selectSubObject({ modelId: clip.modelId, uuid: clip.uuid });
    message.success(`已恢复部件「${clip.name}」`);
    return true;
  }
  const newUuid = handle?.cloneSubObject?.(clip.uuid);
  if (!newUuid) {
    message.error('粘贴失败');
    return false;
  }
  useSceneStore.getState().selectSubObject({ modelId: clip.modelId, uuid: newUuid });
  message.success(`已粘贴部件「${clip.name}」`);
  return true;
}

/** 打开重命名弹窗（菜单/F2 共用） */
export function openRenameSubObject(modelId: string, uuid: string) {
  useSubObjectMenuStore.getState().openRename({ modelId, uuid, name: getSubObjectName(modelId, uuid) });
}

/** 提交重命名（弹窗确认；入撤销历史） */
export function renameSubObject(modelId: string, uuid: string, name: string): boolean {
  const handle = modelRefsRegistry.get(modelId);
  useSceneStore.getState().pushSubObjectSnapshot();
  const ok = handle?.renameSubObject?.(uuid, name);
  if (!ok) {
    message.error('重命名失败');
    return false;
  }
  message.success(`已重命名为「${name}」`);
  return true;
}

/** 聚焦选中部件（相机飞至部件；未选部件时聚焦模型） */
export function focusSelectedSubObject() {
  cameraControlsRegistry.focusSelected?.();
}


/** 2026-09-14：删除当前全部选中部件（多选支持——配合「选中同源部件」批量操作；软删除可 Ctrl+Z 恢复） */
export function deleteSelectedSubObjects(): void {
  const st = useSceneStore.getState();
  const list = st.selectedSubObjects.length > 0
    ? st.selectedSubObjects
    : (st.selectedSubObject ? [st.selectedSubObject] : []);
  if (list.length === 0) {
    message.info('未选中部件');
    return;
  }
  if (list.length === 1) {
    deleteSubObject(list[0].modelId, list[0].uuid);
    return;
  }
  Modal.confirm({
    title: '批量删除部件',
    content: `将删除 ${list.length} 个选中部件（软删除，可 Ctrl+Z 撤销恢复）。确认删除？`,
    okText: '删除',
    okButtonProps: { danger: true },
    cancelText: '取消',
    onOk: () => {
      // 先确保每个目标有"可见"配置（快照完整 → 撤销可全部恢复）
      list.forEach((x) => ensureVisibleConfig(x.modelId, x.uuid));
      useSceneStore.getState().pushSubObjectSnapshot();
      list.forEach((x) => modelRefsRegistry.get(x.modelId)?.removeSubObject?.(x.uuid));
      setSubObjectClipboard(null);
      useSceneStore.getState().selectSubObjects([]);
      message.success(`已删除 ${list.length} 个部件（Ctrl+Z 可恢复）`);
    },
  });
}
